import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiFetch } from "@/utils/api";

interface DashboardLine {
  partId: string;
  partName: string;
  partNumber: string;
  quantityRequired: number;
  onHand: number;
}

interface DashboardSystem {
  id: string;
  name: string;
  buildable: number;
  lines: DashboardLine[];
}

export function DashboardPage() {
  const [systems, setSystems] = useState<DashboardSystem[]>([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const data = await apiFetch<{ systems: DashboardSystem[] }>("/dashboard");
        setSystems(data.systems);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to load dashboard");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <p className="text-muted-foreground">Loading dashboard…</p>;
  if (error) return <p className="text-destructive">{error}</p>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <h2 className="text-2xl font-bold text-[#1e3a4f]">Dashboard</h2>
      </div>

      {systems.length === 0 ? (
        <p className="text-muted-foreground">
          No systems yet. Add systems, parts, and BOM lines in Admin to populate the dashboard.
        </p>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2 xl:grid-cols-3">
          {systems.map((system) => (
            <div key={system.id} className="dashboard-card bg-white rounded-lg shadow p-4">
              <div className="flex items-center justify-between mb-3 border-b pb-2">
                <Link
                  to={`/systems/${system.id}`}
                  className="font-semibold text-[#1e3a4f] hover:underline"
                >
                  {system.name}
                </Link>
                <span className="text-sm font-semibold tabular-nums bg-[#1e3a4f] text-white px-2 py-0.5 rounded">
                  {system.buildable}
                </span>
              </div>
              {system.lines.length > 0 ? (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-muted-foreground border-b">
                      <th className="py-1 pr-2 font-medium">Part</th>
                      <th className="py-1 px-1 font-medium text-right w-12">Req</th>
                      <th className="py-1 pl-1 font-medium text-right w-16">On-hand</th>
                    </tr>
                  </thead>
                  <tbody>
                    {system.lines.map((line) => (
                      <tr key={line.partId} className="border-b border-border/50 last:border-0">
                        <td className="py-1 pr-2">
                          <Link
                            to={`/systems/${system.id}/parts/${line.partId}/history`}
                            className="hover:underline"
                          >
                            {line.partName}
                          </Link>
                        </td>
                        <td className="py-1 px-1 text-right tabular-nums">{line.quantityRequired}</td>
                        <td className="py-1 pl-1 text-right tabular-nums">{line.onHand}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p className="text-sm text-muted-foreground">No BOM lines yet.</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
